import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Folder, Hash, Lock, Trash2 } from 'lucide-react';
import type { ChatSection, Conversation } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useChatSections, useWorkspaceId } from '@/lib/hooks';
import { qk, queryClient } from '@/lib/queryClient';
import { buildSectionTree, sectionPath, type SectionNode } from '@/lib/sectionTree';
import { useUi } from '@/lib/store';
import { Button, Input, Modal, Select, Spinner, Tabs, ask } from '@/components/ui';
import { WorkspaceLooseChannels } from './LooseChannelsChoice';

/** Admin/manager tool: create, rename, reorder and move sections; place channels into sections. */
export function SectionsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const sections = useChatSections();
  const channels = useQuery({ queryKey: ['browse', ws, 'sections'], queryFn: () => api.browseChannels(ws!), enabled: Boolean(ws && open) });
  const [tab, setTab] = useState<'sections' | 'channels'>('sections');
  const [name, setName] = useState('');
  const [parent, setParent] = useState('');
  const list = useMemo(() => sections.data?.sections ?? [], [sections.data]);
  const maxDepth = sections.data?.max_depth ?? 4;
  const tree = useMemo(() => buildSectionTree(list, [], true), [list]);
  const flat = useMemo(() => {
    const out: SectionNode[] = [];
    const walk = (n: SectionNode) => { out.push(n); n.children.forEach(walk); };
    tree.roots.forEach(walk);
    return out;
  }, [tree]);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: qk.chatSections(ws ?? '') });
    await queryClient.invalidateQueries({ queryKey: qk.conversations(ws ?? '') });
    await channels.refetch();
  };
  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    try { await fn(); if (ok) toast(ok, 'success'); await refresh(); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const descendants = (id: string): Set<string> => {
    const out = new Set<string>([id]);
    let grew = true;
    while (grew) { grew = false; for (const s of list) if (s.parent_id && out.has(s.parent_id) && !out.has(s.id)) { out.add(s.id); grew = true; } }
    return out;
  };
  const siblings = (s: ChatSection) => list.filter(x => x.parent_id === s.parent_id).sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
  const move = (s: ChatSection, dir: -1 | 1) => {
    const sib = siblings(s);
    const i = sib.findIndex(x => x.id === s.id);
    const j = i + dir;
    if (j < 0 || j >= sib.length) return;
    const other = sib[j];
    // Swap positions (distinct values even if they were equal).
    const a = other.position === s.position ? s.position + dir : other.position;
    void run(async () => { await api.updateChatSection(s.id, { position: a }); await api.updateChatSection(other.id, { position: s.position }); });
  };

  const channelRows = (channels.data?.channels ?? []).slice().sort((a, b) => (a.slug ?? '').localeCompare(b.slug ?? ''));
  const parentOptions = (exclude?: Set<string>) => flat.filter(n => !exclude?.has(n.section.id) && n.depth < maxDepth - 1);

  return (
    <Modal open={open} onClose={onClose} title="Organise chat sections" wide
      footer={<Button onClick={onClose}>Done</Button>}>
      <Tabs value={tab} onChange={setTab} tabs={[{ id: 'sections', label: 'Sections' }, { id: 'channels', label: 'Channels' }]} />
      {sections.isLoading ? <Spinner /> : tab === 'sections' ? (
        <div className="mt-4 space-y-4">
          <div className="flex gap-2">
            <Input className="flex-1" placeholder="New section name, e.g. Retail Stores" value={name} onChange={e => setName(e.target.value)} />
            <Select className="w-56" value={parent} onChange={e => setParent(e.target.value)}>
              <option value="">Top level</option>
              {parentOptions().map(n => <option key={n.section.id} value={n.section.id}>{'— '.repeat(n.depth)}{n.section.name}</option>)}
            </Select>
            <Button disabled={!name.trim()} onClick={() => void run(() => api.createChatSection(ws!, { name: name.trim(), parent_id: parent || null }), 'Section created').then(() => setName(''))}>Add</Button>
          </div>
          {!flat.length ? <p className="text-sm text-slate-500">No sections yet. Channels appear at the top level until you add some.</p> : (
            <div className="divide-y divide-slate-100 rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-700">
              {flat.map(n => (
                <div key={n.section.id} className="flex items-center gap-2 px-3 py-1.5" style={{ paddingLeft: 12 + n.depth * 20 }}>
                  <Folder className="h-4 w-4 shrink-0 text-orange-500" />
                  <input className="min-w-0 flex-1 rounded border border-transparent bg-transparent px-1 py-0.5 text-sm hover:border-slate-200 focus:border-accent-line focus:outline-none dark:hover:border-slate-700"
                    defaultValue={n.section.name} onBlur={e => e.target.value.trim() && e.target.value.trim() !== n.section.name && void run(() => api.updateChatSection(n.section.id, { name: e.target.value.trim() }), 'Renamed')} />
                  <button title="Move up" className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800" onClick={() => move(n.section, -1)}><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button title="Move down" className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800" onClick={() => move(n.section, 1)}><ArrowDown className="h-3.5 w-3.5" /></button>
                  <Select className="h-7 w-44 py-0 text-xs" value={n.section.parent_id ?? ''} title="Move into"
                    onChange={e => void run(() => api.updateChatSection(n.section.id, { parent_id: e.target.value || null }), 'Moved')}>
                    <option value="">Top level</option>
                    {parentOptions(descendants(n.section.id)).map(p => <option key={p.section.id} value={p.section.id}>{'— '.repeat(p.depth)}{p.section.name}</option>)}
                  </Select>
                  <button title="Delete section (contents move up a level)" className="rounded p-1 text-slate-500 hover:bg-rose-50 hover:text-rose-600"
                    onClick={() => void ask(`Delete "${n.section.name}"? Its channels and sub-sections move up one level.`).then(ok => { if (ok) return run(() => api.deleteChatSection(n.section.id), 'Section deleted'); })}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          )}
          <WorkspaceLooseChannels />
          <p className="text-xs text-slate-500">Sections are navigation only - they never give anyone access to a channel. People only see sections that contain channels they can open.</p>
        </div>
      ) : (
        <div className="mt-4 max-h-[55vh] divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-700">
          {channels.isLoading ? <Spinner /> : channelRows.map((c: Conversation) => (
            <div key={c.id} className="flex items-center gap-2 px-3 py-1.5 text-sm">
              {c.is_private ? <Lock className="h-3.5 w-3.5 text-slate-500" /> : <Hash className="h-3.5 w-3.5 text-slate-500" />}
              <span className="flex-1 truncate">{c.slug}</span>
              <span className="hidden truncate text-xs text-slate-500 sm:inline">{sectionPath(list, c.section_id) || 'Top level'}</span>
              <Select className="h-7 w-52 py-0 text-xs" value={c.section_id ?? ''} onChange={e => void run(() => api.placeConversation(c.id, { section_id: e.target.value || null }), `Moved #${c.slug}`)}>
                <option value="">Top level</option>
                {flat.map(n => <option key={n.section.id} value={n.section.id}>{'— '.repeat(n.depth)}{n.section.name}</option>)}
              </Select>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
